import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { DatePicker } from '@domgen/base-design-system-2.0/molecules/DatePicker';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { toLocalDateString } from '@domgen/shared/utils/date/toLocalDateString';
import { usePostHog } from '@posthog/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from '../assets/icons/ArrowLeft';
import { PageBackgroundWrapper } from '../components/PageBackgroundWrapper';
import { FormKey } from '../enums/formKey';
import { StepKey } from '../enums/stepKey';
import { StoreEvent } from '../enums/storeEvent';
import { useFormValidation } from '../hooks/useFormValidation';
import { capturePostHogEvent } from '../services/posthog/capture';
import { useApplianceFormData } from '../store/selectors/useApplianceFormData';
import { useAppState } from '../store/selectors/useAppState';
import { useUrlParams } from '../store/selectors/useUrlParams';
import { store } from '../store/store';
import { formValidators } from '../utils/form/validators';
import { getSystemCode } from '../utils/getSystemCode';
import { formApplianceName } from '../utils/store/formApplianceName';
import { formCategoryName } from '../utils/store/formCategoryName';

type PurchaseDatePageProps = {
  stepNumber: number;
  totalSteps: number;
};

export function ArrivalDatePage({ stepNumber, totalSteps }: PurchaseDatePageProps) {
  const { t } = useTranslation(['arrivalDatePage', 'common']);
  const formData = useApplianceFormData();
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const posthog = usePostHog();
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();

  // Min date is set to 8 years
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 8);

  const { formValues, errors, handleChange, validateAll } = useFormValidation(
    {
      [FormKey.ARRIVAL_DATE]: formData[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || '',
    },
    {
      [FormKey.ARRIVAL_DATE]: [formValidators.required(t('errors.required'))],
    },
  );
  const arrivalDateValue = formValues[FormKey.ARRIVAL_DATE];
  const selectedDate = arrivalDateValue ? parseLocalDateString(arrivalDateValue) : undefined;

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'purchase-date',
    });
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const formattedDate = formatLocalisedDate(selectedDate, 'en-US');
      gaEvent({
        eventCategory: 'appliance details form USA',
        eventAction: 'purchaseDate',
        eventLabel: `${formattedDate}`,
        category: 'registration-pages',
        type: 'STANDARD',
        applianceCategory,
        applianceName,
        appSessionID: session?.id,
        pagename: 'purchase-date',
        regSystemCode: systemCode,
      });
    }
  }, [selectedDate]);

  const handleContinue = () => {
    const isValid = validateAll();

    gaEvent({
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: isValid ? 'success' : 'failed',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'purchase-date',
      regSystemCode: systemCode,
    });

    capturePostHogEvent(posthog, 'date-continue-cta-clicked', store.get());

    if (!isValid) {
      return;
    }

    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.ARRIVAL_DATE]: {
          purchaseDate: arrivalDateValue,
        },
      },
    });
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'purchase-date',
      regSystemCode: systemCode,
    });
    capturePostHogEvent(posthog, 'date-back-clicked', store.get());
    store.send({ type: StoreEvent.BACK, data: { stepKey: StepKey.ARRIVAL_DATE } });
  };

  return (
    <PageBackgroundWrapper>
      <ProgressBars total={totalSteps} current={stepNumber} />

      <h1 className="my-(--spacing-space-400) text-h3">{t('heading')}</h1>
      <p className="mb-4">{t('description')}</p>

      <div className="flex flex-col gap-2">
        <DatePicker
          value={selectedDate}
          onChange={(date) => {
            handleChange({
              target: {
                name: FormKey.ARRIVAL_DATE,
                value: date ? toLocalDateString(date) : '',
              },
            });
          }}
          minDate={minDate}
          maxDate={new Date()}
          label={t('label')}
          error={errors[FormKey.ARRIVAL_DATE]}
          errors={{
            maxDate: t('errors.invalid'),
            invalidDate: t('errors.invalid'),
          }}
        />
      </div>

      <div className="flex flex-col w-full mt-4 items-center md:flex-row-reverse md:justify-between">
        <div className="w-full md:w-fit mb-4">
          <Button onClick={handleContinue}>{t('common:continue')}</Button>
        </div>
        <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
          {t('common:back')}
        </TextLink>
      </div>
    </PageBackgroundWrapper>
  );
}
